import { useState, useCallback } from 'react';

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

export interface ToastState {
    open: boolean;
    message: string;
    severity: ToastSeverity;
}

export interface ToastOptions {
    severity?: ToastSeverity;
    duration?: number;
}

export interface UseToastReturn {
    toast: ToastState;
    showToast: (message: string, severity?: ToastSeverity) => void;
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
    showInfo: (message: string) => void;
    showWarning: (message: string) => void;
    hideToast: () => void;
    handleClose: (event?: React.SyntheticEvent | Event, reason?: string) => void;
}

/**
 * Единый хук для управления уведомлениями (тостами)
 * Обеспечивает консистентное поведение на всех страницах
 *
 * @example
 * const { toast, showSuccess, showError, handleClose } = useToast();
 *
 * // Показать успешное уведомление
 * showSuccess('Операция выполнена успешно');
 *
 * // Показать ошибку
 * showError('Произошла ошибка');
 *
 * // В JSX
 * <Snackbar open={toast.open} onClose={handleClose}>
 *   <Alert onClose={handleClose} severity={toast.severity}>
 *     {toast.message}
 *   </Alert>
 * </Snackbar>
 */
export const useToast = (): UseToastReturn => {
    const [toast, setToast] = useState<ToastState>({
        open: false,
        message: '',
        severity: 'info'
    });

    const showToast = useCallback((message: string, severity: ToastSeverity = 'info') => {
        setToast({
            open: true,
            message,
            severity
        });
    }, []);

    const showSuccess = useCallback((message: string) => {
        showToast(message, 'success');
    }, [showToast]);

    const showError = useCallback((message: string) => {
        showToast(message, 'error');
    }, [showToast]);

    const showInfo = useCallback((message: string) => {
        showToast(message, 'info');
    }, [showToast]);

    const showWarning = useCallback((message: string) => {
        showToast(message, 'warning');
    }, [showToast]);

    const hideToast = useCallback(() => {
        setToast(prev => ({ ...prev, open: false }));
    }, []);

    const handleClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
        // Не закрываем при клике вне области тоста
        if (reason === 'clickaway') {
            return;
        }
        hideToast();
    }, [hideToast]);

    return {
        toast,
        showToast,
        showSuccess,
        showError,
        showInfo,
        showWarning,
        hideToast,
        handleClose
    };
};
